import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/apiConfig'; 
import clientPrinterService from '../services/clientPrinterService';
import './DetallePedido.css';

const AgregarProductosComponent = () => {
  const { pedidoId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  
  const [pedido, setPedido] = useState(null);
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('todas');
  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [productosSeleccionados, setProductosSeleccionados] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mensajeExito, setMensajeExito] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        
        // Cargar datos del pedido actual
        const responsePedido = await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
          headers: {
            'Authorization': `Bearer ${auth?.token}`
          }
        });
        
        if (!responsePedido.ok) {
          throw new Error('Error al cargar los detalles del pedido');
        }
        
        const dataPedido = await responsePedido.json();
        setPedido(dataPedido);
        
        // Cargar productos disponibles
        const responseProductos = await fetch(getApiUrl('/productos'), {
          headers: {
            'Authorization': `Bearer ${auth?.token}`
          }
        });

        if (!responseProductos.ok) {
          throw new Error('Error al cargar productos');
        }

        const dataProductos = await responseProductos.json();
        // Normalizar precios y costos
        const productosNormalizados = dataProductos.map(p => ({
          ...p,
          precio: typeof p.precio === 'string' ? parseFloat(p.precio) : p.precio,
          costo: typeof p.costo === 'string' ? parseFloat(p.costo) : p.costo
        }));
        setProductos(productosNormalizados);
        
        // Extraer categorías únicas
        const categoriasUnicas = [...new Set(dataProductos.map(p => p.categoria))];
        setCategorias(categoriasUnicas);
        
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError(err.message || 'Error al cargar datos');
        setLoading(false);
      }
    };
    
    cargarDatos();
  }, [pedidoId, auth?.token]);

  // FILTRADO POR CATEGORÍA Y BÚSQUEDA
  const filtrarProductos = () => {
    let resultado = productos;
    if (categoriaSeleccionada !== 'todas') {
      resultado = resultado.filter(p => p.categoria === categoriaSeleccionada);
    }
    if (busquedaProducto.trim()) {
      resultado = resultado.filter(p =>
        p.nombre.toLowerCase().includes(busquedaProducto.toLowerCase())
      );
    }
    return resultado;
  };

  const agregarProducto = (producto) => {
    // Verificar si el producto ya está en la selección
    const productoExistente = productosSeleccionados.find(item => item.id === producto.id);
    
    if (productoExistente) {
      // Incrementar cantidad
      setProductosSeleccionados(productosSeleccionados.map(item => 
        item.id === producto.id ? { ...item, cantidad: item.cantidad + 1 } : item
      ));
    } else {
      // Agregar nuevo producto
      setProductosSeleccionados([...productosSeleccionados, { ...producto, cantidad: 1 }]);
    }
  };

  const quitarProducto = (productoId) => {
    setProductosSeleccionados(productosSeleccionados.filter(item => item.id !== productoId));
  };

  const actualizarCantidadProducto = (productoId, nuevaCantidad) => {
    if (nuevaCantidad <= 0) {
      quitarProducto(productoId);
      return;
    }
    
    setProductosSeleccionados(productosSeleccionados.map(item => 
      item.id === productoId ? { ...item, cantidad: nuevaCantidad } : item
    ));
  };

  const calcularTotal = () => {
    return productosSeleccionados.reduce((total, item) => total + (item.precio * item.cantidad), 0).toFixed(2);
  };

  const guardarProductosAdicionales = async () => {
    if (productosSeleccionados.length === 0) {
      setError('No hay productos seleccionados');
      return;
    }
    
    setGuardando(true);
    try {
      // Preparar los datos para el nuevo endpoint específico
      const productosData = productosSeleccionados.map(item => ({
        id_producto: item.id,
        cantidad: item.cantidad,
        notas: '' // Si necesitas agregar notas por producto
      }));
      
      const datosEnvio = {
        productos: productosData,
        observaciones: observaciones || ''
      };
      
      console.log('Enviando datos para agregar productos:', datosEnvio);
      
      // Usar el nuevo endpoint específico para agregar productos
      const response = await fetch(getApiUrl(`/pedidos/${pedidoId}/agregar-productos`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify(datosEnvio)
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Error al agregar productos: ${errorData}`);
      }
      
      const responseData = await response.json();
      console.log('Respuesta del servidor:', responseData);
      
      // Auto-imprimir comanda en cliente para los nuevos platos agregados
      try {
        const pisoMesa = pedido?.piso ?? pedido?.numero_piso ?? pedido?.piso_mesa ?? auth?.user?.piso_asignado ?? 1;
        const mesaNumero = pedido?.numero ?? pedido?.numero_mesa;
        const mesaFormateada = mesaNumero 
          ? (String(mesaNumero).toLowerCase().startsWith('mesa') ? String(mesaNumero) : `Mesa ${mesaNumero}`)
          : (pedido?.id_mesa === 910100 ? 'DIRECTO' : (pedido?.mesa || `Mesa ${pedido?.id_mesa || ''}`));

        const datosComanda = {
          ordenId: parseInt(pedidoId),
          id: parseInt(pedidoId),
          mesa: mesaFormateada,
          piso: pisoMesa,
          mozo: auth?.user?.nombre || auth?.user?.usuario || 'Mozo',
          fecha: new Date().toLocaleDateString('es-PE'),
          hora: new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
          items: productosSeleccionados.map(item => ({
            nombre: item.nombre,
            cantidad: item.cantidad,
            notas: item.notas || '',
            categoria: item.categoria_nombre || item.categoria || item.nombre_categoria || item.tiempo || 'GENERAL'
          })),
          notaGeneral: observaciones || ''
        };

        await clientPrinterService.imprimirComandaCliente(datosComanda, 'NUEVA');
      } catch (printErr) {
        console.error('Error al enviar comanda de platos adicionales a la impresora:', printErr);
      }

      // Mostrar mensaje de éxito
      setMensajeExito('¡Productos agregados correctamente al pedido!');
      
      // Limpiar selección
      setProductosSeleccionados([]);
      setObservaciones('');
      
      // Redirigir inmediatamente
      navigate(`/pedidos/${pedidoId}`);
    } catch (err) {
      console.error('Error al agregar productos:', err);
      setError(err.message || 'Error al agregar productos al pedido');
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">Error: {error}</div>
        <button onClick={() => navigate(`/pedido/${pedidoId}`)} className="btn-volver">
          Volver al Pedido
        </button>
      </div>
    );
  }

  return (
    <div className="agregar-productos-container">
      <div className="agregar-productos-header">
        <h2>Agregar Productos - Pedido #{pedidoId}</h2>
        <div className="botones-navegacion">
          <button onClick={() => navigate(`/pedidos/${pedidoId}`)} className="btn-volver">
            ← Volver al Pedido
          </button>
        </div>
      </div>

      {mensajeExito && (
        <div className="mensaje-exito">
          {mensajeExito}
        </div>
      )}

      <div className="nuevo-pedido-grid">
        <div className="productos-section">
          <div className="buscador-productos">
            <input
              type="text"
              placeholder="Buscar producto..."
              value={busquedaProducto}
              onChange={(e) => setBusquedaProducto(e.target.value)}
              className="input-busqueda-producto"
            />
            {busquedaProducto && (
              <button className="btn-limpiar-busqueda" onClick={() => setBusquedaProducto('')}>×</button>
            )}
          </div>
          <div className="categorias-tabs">
            <button 
              className={categoriaSeleccionada === 'todas' ? 'active' : ''}
              onClick={() => setCategoriaSeleccionada('todas')}
            >
              Todas
            </button>
            {categorias.map(categoria => (
              <button 
                key={categoria}
                className={categoriaSeleccionada === categoria ? 'active' : ''}
                onClick={() => setCategoriaSeleccionada(categoria)}
              >
                {categoria}
              </button>
            ))}
          </div>

          <div className="productos-grid">
            {filtrarProductos().map(producto => (
              <div 
                key={producto.id} 
                className="producto-card"
                onClick={() => agregarProducto(producto)}
              >
                <div className="producto-nombre">{producto.nombre}</div>
                <div className="producto-precio">S/. {producto.precio.toFixed(2)}</div>
                <div className="producto-stock">Stock: {producto.stock_actual} {producto.unidad_medida}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="resumen-pedido">
          <h3>Productos Seleccionados</h3>
          
          {productosSeleccionados.length === 0 ? (
            <div className="pedido-vacio">
              <p>Selecciona productos para agregar al pedido</p>
            </div>
          ) : (
            <>
              <div className="items-pedido">
                {productosSeleccionados.map(item => (
                  <div key={item.id} className="item-pedido">
                    <div className="item-info">
                      <div className="item-nombre">{item.nombre}</div>
                      <div className="item-precio">S/. {item.precio.toFixed(2)}</div>
                    </div>
                    <div className="item-controles">
                      <button 
                        className="btn-cantidad"
                        onClick={(e) => {
                          e.stopPropagation();
                          actualizarCantidadProducto(item.id, item.cantidad - 1);
                        }}
                      >
                        -
                      </button>
                      <span className="item-cantidad">{item.cantidad}</span>
                      <button 
                        className="btn-cantidad"
                        onClick={(e) => {
                          e.stopPropagation();
                          actualizarCantidadProducto(item.id, item.cantidad + 1);
                        }}
                      >
                        +
                      </button>
                      <button 
                        className="btn-eliminar"
                        onClick={(e) => {
                          e.stopPropagation();
                          quitarProducto(item.id);
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div className="item-subtotal">
                      S/. {(item.precio * item.cantidad).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="observaciones">
                <label htmlFor="observaciones">Observaciones:</label>
                <textarea
                  id="observaciones"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Instrucciones adicionales..."
                  rows={3}
                />
              </div>

              <div className="total-pedido">
                <div className="label">Total Adicional:</div>
                <div className="monto">S/. {calcularTotal()}</div>
              </div>

              <button 
                className="btn-agregar-al-pedido"
                onClick={guardarProductosAdicionales}
                disabled={guardando || productosSeleccionados.length === 0}
              >
                {guardando ? '⏳ Imprimiendo comanda...' : 'Agregar al Pedido'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgregarProductosComponent;